import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ClientWorkspace from './ClientWorkspace';
import CompanyLogo from '../CompanyLogo';
import { requestUnderstandLeave } from '../../utils/understandLeave';

const STORAGE_KEY = 'clientWorkspaceDrawerWidth';
const DEFAULT_WIDTH = 720;
const MIN_WIDTH = 420;
const MAX_WIDTH_RATIO = 0.92;

const clampWidth = (width) => {
  const maxWidth = Math.floor(window.innerWidth * MAX_WIDTH_RATIO);
  return Math.min(Math.max(width, MIN_WIDTH), maxWidth);
};

const getInitialWidth = () => {
  try {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    if (Number.isFinite(saved) && saved > 0) return clampWidth(saved);
  } catch {
    // ignore storage errors
  }
  return clampWidth(DEFAULT_WIDTH);
};

/**
 * Desktop-only container for Client Workspace.
 * Mobile can later render <ClientWorkspace /> full-screen without this drawer.
 */
const ClientWorkspaceDrawer = ({ open, onClose, product, company, initialTab }) => {
  const website = product?.website || company?.website || product?.company?.website;
  const companyName = company?.companyName || product?.company?.companyName;
  const productName = product?.productName;
  const logoUrl = company?.companyLogo || product?.company?.companyLogo;

  const [width, setWidth] = useState(getInitialWidth);
  const dragStateRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWidth((current) => clampWidth(current));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const stopDragging = useCallback(() => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragStateRef.current) return;
      const nextWidth = clampWidth(window.innerWidth - event.clientX);
      setWidth(nextWidth);
    };

    const handlePointerUp = () => {
      if (!dragStateRef.current) return;
      setWidth((current) => {
        try {
          localStorage.setItem(STORAGE_KEY, String(current));
        } catch {
          // ignore storage errors
        }
        return current;
      });
      stopDragging();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      stopDragging();
    };
  }, [stopDragging]);

  const requestClose = () => requestUnderstandLeave(onClose);

  const handleResizeStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current = { startX: event.clientX, startWidth: width };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={requestClose}
      PaperProps={{
        sx: {
          width,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
        },
      }}
    >
      <Box
        onPointerDown={handleResizeStart}
        role='separator'
        aria-orientation='vertical'
        aria-label='Resize workspace'
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={Math.floor(window.innerWidth * MAX_WIDTH_RATIO)}
        aria-valuenow={width}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 8,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 2,
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover .resize-grip, &:active .resize-grip': {
            bgcolor: 'primary.main',
            opacity: 1,
          },
        }}
      >
        <Box
          className='resize-grip'
          sx={{
            width: 3,
            height: 48,
            borderRadius: 999,
            bgcolor: 'divider',
            opacity: 0.9,
            transition: 'background-color 0.15s ease, opacity 0.15s ease',
          }}
        />
      </Box>

      <Box display='flex' alignItems='center' justifyContent='space-between' gap={2} px={2} py={1.5} flexShrink={0}>
        <Box display='flex' alignItems='center' gap={1.5} minWidth={0} flex={1}>
          <IconButton onClick={requestClose} aria-label='Close workspace' size='small'>
            <CloseIcon />
          </IconButton>

          <Box display='flex' alignItems='center' gap={1.5} minWidth={0}>
            <CompanyLogo url={logoUrl} size='48px' />
            <Box minWidth={0}>
              <Typography variant='h6' fontWeight={700} noWrap>
                {companyName || 'Company'}
              </Typography>
              {productName && (
                <Typography variant='body2' color='text.secondary' noWrap>
                  {productName}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box display='flex' alignItems='center' gap={1} flexShrink={0}>
          {website && (
            <Button
              component='a'
              href={website}
              target='_blank'
              rel='noopener noreferrer'
              variant='outlined'
              size='small'
              color='inherit'
              endIcon={<OpenInNewIcon />}
              sx={{
                textTransform: 'none',
                color: 'text.primary',
                borderColor: 'divider',
                height: 36,
                px: 1.5,
                py: 0.75,
                '&:hover': {
                  borderColor: 'grey.400',
                  bgcolor: 'grey.50',
                },
              }}
            >
              Open website
            </Button>
          )}
          <IconButton
            aria-label='More options'
            size='small'
            sx={{
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              width: 36,
              height: 36,
              '&:hover': {
                borderColor: 'grey.400',
                bgcolor: 'grey.50',
              },
            }}
          >
            <MoreHorizIcon fontSize='small' />
          </IconButton>
        </Box>
      </Box>

      <Box flex={1} minHeight={0} display='flex' flexDirection='column'>
        <ClientWorkspace product={product} company={company} initialTab={initialTab} />
      </Box>
    </Drawer>
  );
};

export default ClientWorkspaceDrawer;
